import { ICartDetailsPetCartItemData } from "@/api_framework/api_modals/Cart";
import { IInventoryStockItems, IStockIncompleteFormStockItemsPostBody } from "@/api_framework/api_modals/Inventory";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";

import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useUtils } from "@/app/hooks/useUtils";
import { format } from "date-fns";
import { toNumber } from "lodash";
import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import AlertBlue from "@/app/components/ui/Alerts/Blue";
import MandatoryLabel from "@/app/components/ui/Reusable/MandatoryLabel";
import { useStockItemsPostAPI } from "@/app/hooks/api_hooks/Inventory/StockIncompleteForm/useStockItemsPostAPI";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import CartProductSearchComponent from "../../auth_routes/CartManagement/CartProductSearchComponent";

export interface ICartStockIncompleteModalState {
  stockOutItems: ICartDetailsPetCartItemData[];
}

interface ICartStockIncompleteForm extends IStockIncompleteFormStockItemsPostBody {}

const CartStockIncompleteModal = () => {
  const { setInvisible } = useModalActions("CART_STOCK_INCOMPLETE_MODAL");
  const { stockOutItems } = useModalState("CART_STOCK_INCOMPLETE_MODAL") as ICartStockIncompleteModalState;
  const isStockInCompleteInProgress = useLoader("isStockInCompleteInProgress");
  const { execute: submitStockIncompleteRequest } = useStockItemsPostAPI();
  const { getRouteKey } = useRouter();
  const { customerDetails, userProfile, customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"))?.routeState
    ?.state as ICustomerProfileState;
  const { mappedClinicName } = useAppSelector((state) => state?.root.UIStates);

  const { loggedInUserEmail } = useAppSelector((state) => {
    return {
      loggedInUserEmail: state.auth.user.email,
      loggedInUserId: state?.auth?.userLoginInfo?.user_id,
    };
  });
  const { fieldDOBFormat } = useStringConstants();
  const { getCurrentDate } = useUtils();
  const formHook = useForm<ICartStockIncompleteForm>({
    defaultValues: {
      item_name: [],
    },
  });

  const formArrayHook = useFieldArray<ICartStockIncompleteForm>({
    name: "item_name",
    control: formHook.control,
  });

  const handleSubmit = () => {
    formHook.trigger().then((isValid) => {
      if (isValid) {
        let data = null;
        data = {
          ...formHook.getValues(),
          source: "CART",
        };

        if (data.item_name?.length === 0) {
          toast.error("Please add products to be requested.");
          return;
        }
        submitStockIncompleteRequest(data).then(() => {
          formHook.setValue("remarks", "");
          setInvisible();
        });
      }
    });
  };

  const reduceOutOfStockItems = (): IInventoryStockItems[] => {
    return stockOutItems?.map((sti) => {
      return {
        category: sti?.category ?? "product",
        item_name_sub: sti.name,
        quantity: 1,
      };
    });
  };

  useEffect(() => {
    if (stockOutItems) {
      const reducedStockOutItems = reduceOutOfStockItems();
      formHook.setValue("item_name", reducedStockOutItems);
      formHook.setValue("required_on", getCurrentDate());
      formHook.setValue("client_id", customerId);
      formHook.setValue("client_name", customerDetails?.name);
      formHook.setValue("name_of_requester", loggedInUserEmail);
      formHook.setValue("user_id", customerId);
      formHook.setValue("clinic_name", mappedClinicName);
    }
  }, [stockOutItems]);

  return (
    <ModalPrompt
      modalId="CART_STOCK_INCOMPLETE_MODAL"
      dialogClassName="sm:my-8 sm:w-full sm:max-w-5xl sm:p-6 overflow-hidden"
      button={{
        primaryButtonText: isStockInCompleteInProgress ? "Sending..." : "Send Request",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: "bg-blue-800 text-white",
        primaryCallback: () => {
          handleSubmit();
          //   setInvisible();
        },

        secondaryCallback: () => {
          setInvisible();
        },
        onCrossClick: () => {
          formHook.reset();
          setInvisible();
        },

        shouldHideOnClick: false,
      }}
      isLoading={isStockInCompleteInProgress}
      title="Stock Incomplete Form"
      content={
        <div className="mt-8 flow-root">
          <div className="flex justify-start items-center gap-x-2">
            <MandatoryLabel className="text-xs" label="Required On" isMandatory />
            <PWLCalendarComponent
              errorMessages={[{ type: "required", message: "Required" }]}
              fieldError={formHook.formState.errors.required_on}
              register={formHook.register("required_on", {
                required: true,
              })}
              showTypedErrors
              disablePastMonthSelection
              disableDatesBeforeDate={getCurrentDate()}
              showFutureYears
              defaultSelectedDate={formHook.watch("required_on")}
              onSelectDate={(date) => {
                formHook.setValue("required_on", format(date, fieldDOBFormat));
              }}
            />
          </div>

          <div className="mt-2">
            <CartProductSearchComponent
              placeholder="Search Product , Service , BOM"
              containerClassName="w-full"
              onSelectItem={(item) => {
                formArrayHook.append({
                  category: item?.category ?? "product",
                  item_name_sub: item.name,
                  quantity: 1,
                });
              }}
              inputContainerClassName="w-full"
            />
          </div>

          {formArrayHook?.fields?.length > 0 ? (
            <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-2 mt-4 overflow-x-auto">
              <div className="inline-block sm:px-6 lg:px-8 py-2 min-w-full align-middle">
                <div className="ring-opacity-5 shadow sm:rounded-lg ring-1 ring-black overflow-hidden">
                  <table className="divide-y divide-gray-300 min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pr-3 pl-4 sm:pl-6 font-semibold text-gray-900 text-left text-sm"
                        >
                          Product Name
                        </th>
                        <th scope="col" className="px-3 py-3.5 font-semibold text-gray-900 text-left text-sm">
                          Add Qty
                        </th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formArrayHook.fields?.map((field, index) => (
                        <tr key={field.id}>
                          <td className="py-4 pr-3 pl-4 sm:pl-6 font-medium text-gray-900 text-sm whitespace-break-spaces">
                            {field?.item_name_sub}
                          </td>
                          <td className="px-3 py-4 text-gray-500 text-sm whitespace-nowrap">
                            <div className="w-[100px]">
                              <InputWithStartAndEndAddOn
                                inputClassName="min-m-10"
                                startAddon="-"
                                startAddOnCallback={() => {
                                  const currentQuantity = formHook.watch(`item_name.${index}.quantity`) ?? 1;
                                  if (currentQuantity > 1) {
                                    formHook.setValue(`item_name.${index}.quantity`, currentQuantity - 1);
                                  }
                                }}
                                step={1}
                                endAddon="+"
                                endAddOnCallback={() => {
                                  const currentQuantity = formHook.watch(`item_name.${index}.quantity`);

                                  if (!currentQuantity) {
                                    formHook.setValue(`item_name.${index}.quantity`, currentQuantity + 1);
                                  } else {
                                    const currentQuantity = formHook.watch(`item_name.${index}.quantity`) ?? 1;
                                    formHook.setValue(`item_name.${index}.quantity`, currentQuantity + 1);
                                  }
                                }}
                                min={1}
                                value={formHook.watch(`item_name.${index}.quantity`) ?? 1}
                                onBlur={(e) => {
                                  const value = toNumber(e.target.value);
                                  if (!value || isNaN(value)) {
                                    // Reset to default value if the input is invalid
                                    formHook.setValue(`item_name.${index}.quantity`, 1);
                                  }
                                }}
                                onChange={(e: any) => {
                                  const inputValue = e.target.value;

                                  // Allow empty input temporarily
                                  if (inputValue === "") {
                                    formHook.setValue(`item_name.${index}.quantity`, "" as any);
                                    return;
                                  }

                                  const value = toNumber(inputValue);

                                  // Validate numeric input and set value
                                  if (!isNaN(value) && value > 0) {
                                    formHook.setValue(`item_name.${index}.quantity`, value);
                                  }
                                }}
                                type="number"
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className=""
                              onClick={(e) => {
                                e.preventDefault();
                                formArrayHook.remove(index);
                              }}
                            >
                              <TrashIcon className="w-5 h-5 text-red-500" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <AlertBlue title="Search and Add products" />
            </div>
          )}
          <div className="mt-4 w-full">
            <textarea
              placeholder="Any remarks while requesting the stock."
              {...formHook.register("remarks", { required: false })}
              rows={5}
              className="border rounded-lg w-full"
            />
          </div>
        </div>
      }
    />
  );
};

export default CartStockIncompleteModal;
